import React, { useRef } from 'react';
import { Box, CircularProgress, IconButton, InputBase, Typography } from '@mui/material';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { AI_AGENT_UI } from './tokens';

type Props = {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
};

/** Script-style composer — AI Agent page only. */
export function AgentPromptInput({
  value,
  onChange,
  onSubmit,
  disabled,
  loading,
  placeholder = 'Send a message',
}: Props) {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const submit = () => {
    if (disabled || loading || !value.trim()) return;
    onSubmit();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Enter sends; Shift+Enter keeps a newline in the multiline field.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      submit();
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', mx: 'auto', flexShrink: 0 }}>
      <Box
        sx={{
          borderRadius: `${AI_AGENT_UI.radius.lg}px`,
          bgcolor: AI_AGENT_UI.surface,
          border: `1px solid ${AI_AGENT_UI.border}`,
          boxShadow: AI_AGENT_UI.shadow,
          px: 2,
          pt: 1.5,
          pb: 1.25,
          transition: 'border-color 0.15s ease',
          '&:focus-within': {
            borderColor: 'rgba(29, 78, 216, 0.45)',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <InputBase
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={5}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={disabled || loading}
            placeholder={placeholder}
            sx={{
              px: 0.25,
              py: 0.25,
              fontSize: AI_AGENT_UI.font,
              fontWeight: 400,
              color: AI_AGENT_UI.text,
              '& textarea': { lineHeight: AI_AGENT_UI.lineHeight },
              '& textarea::placeholder': {
                fontSize: AI_AGENT_UI.font,
                opacity: 0.72,
                color: AI_AGENT_UI.textSecondary,
              },
            }}
          />
          <IconButton
            onClick={submit}
            disabled={disabled || loading || !value.trim()}
            aria-label="Send"
            size="small"
            sx={{
              color: '#fff',
              flexShrink: 0,
              mt: 0.15,
              width: 36,
              height: 36,
              bgcolor: AI_AGENT_UI.primary,
              borderRadius: '8px',
              '&:hover': { bgcolor: AI_AGENT_UI.primaryDark },
              '&.Mui-disabled': { color: '#fff', bgcolor: AI_AGENT_UI.borderStrong },
            }}
          >
            {loading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <SendRoundedIcon sx={{ fontSize: 22 }} />
            )}
          </IconButton>
        </Box>
      </Box>
      <Typography
        sx={{
          mt: 1.25,
          textAlign: 'center',
          fontSize: '0.7rem',
          color: AI_AGENT_UI.textSecondary,
          lineHeight: 1.4,
        }}
      >
        DragonflyTest AI may make mistakes. Review projects, environments, and requests before running
        flows.
      </Typography>
    </Box>
  );
}
